import {
  dayLabel,
  monthOf,
  todayIn,
  type CategoryRef,
  type InboxItemDto,
  type PlanGroupDto,
} from '@koverta/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '../components/toast';
import {
  Avatar,
  Btn,
  CatPill,
  Confidence,
  CurrencyChip,
  EmptyInbox,
  Icon,
  Kbd,
  MerchantLogo,
  Sheet,
  Spinner,
  hhmm,
  relTime,
} from '../components/ui';
import { errorMessage, post } from '../lib/api';
import { useIsMobile } from '../lib/hooks';
import { useHousehold, type Fmt } from '../lib/household';
import { sendOrQueue } from '../lib/outbox';
import { invalidateScopes, useAccounts, useInbox, usePlan } from '../lib/queries';

interface Edits {
  categoryId?: string | null;
  memo?: string;
  remember?: boolean;
}

/** Shared Inbox state: optimistic removal with rollback, per-item edits, and the last action for Undo. */
function useInboxModel() {
  const { toast } = useToast();
  const { today, fmt } = useHousehold();
  const q = useInbox();
  const plan = usePlan(monthOf(today));
  const accountsQ = useAccounts().data;
  const accounts = useMemo(() => accountsQ ?? [], [accountsQ]);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [edits, setEdits] = useState<Record<string, Edits>>({});
  const items = useMemo(() => (q.data?.items ?? []).filter((i) => !hidden.has(i.id)), [q.data, hidden]);
  const groups = useMemo(
    () =>
      (plan.data?.groups ?? [])
        .filter((g) => !g.hidden)
        .map((g) => ({ ...g, categories: g.categories.filter((c) => !c.hidden) })),
    [plan.data],
  );
  const cats = useMemo(() => groups.flatMap((g) => g.categories), [groups]);
  const catById = useMemo(() => new Map(cats.map((c) => [c.id, c])), [cats]);
  const catOf = (it: InboxItemDto) => {
    const e = edits[it.id];
    const id = e && 'categoryId' in e ? e.categoryId : it.categoryId;
    return id ? catById.get(id) : undefined;
  };
  const isReady = (it: InboxItemDto) => !it.needsDecision && (it.isTest || (!!it.accountId && !!catOf(it)));
  const setEdit = (id: string, e: Edits) => setEdits((all) => ({ ...all, [id]: { ...all[id], ...e } }));

  const undo = useCallback(
    async (id: string) => {
      setHidden((h) => {
        const n = new Set(h);
        n.delete(id);
        return n;
      });
      try {
        await post(`/api/inbox/${id}/undo`);
      } catch (e) {
        toast(errorMessage(e));
      }
      invalidateScopes(['inbox', 'transactions', 'plan', 'accounts']);
    },
    [toast],
  );

  const resolve = useCallback(
    async (it: InboxItemDto, how: 'approve' | 'dismiss' | 'merge' | 'keep-both') => {
      const e = edits[it.id] ?? {};
      const cat = catOf(it);
      setHidden((h) => new Set(h).add(it.id));
      const body =
        how === 'approve' || how === 'keep-both'
          ? {
              ...('categoryId' in e ? { categoryId: e.categoryId as CategoryRef } : {}),
              ...(e.memo ? { memo: e.memo } : {}),
              ...(e.remember ? { rememberRule: true } : {}),
            }
          : {};
      try {
        const r = await sendOrQueue({
          method: 'POST',
          path: `/api/inbox/${it.id}/${how}`,
          body,
          label: `${how} ${it.payeeName}`,
        });
        const amount = fmt.money(it.amount, it.currency);
        const msg =
          how === 'approve' || how === 'keep-both' ? (
            <>
              ✓ Approved <b>{it.payeeName}</b> · {amount}
              {cat ? ` → ${cat.name}` : ''}
            </>
          ) : how === 'merge' ? (
            <>
              Merged <b>{it.payeeName}</b> with the existing transaction
            </>
          ) : (
            <>
              Dismissed <b>{it.payeeName}</b>
            </>
          );
        toast(
          r.queued ? <>{msg} · will sync when online</> : msg,
          r.queued ? {} : { undo: () => void undo(it.id) },
        );
      } catch (x) {
        setHidden((h) => {
          const n = new Set(h);
          n.delete(it.id);
          return n;
        });
        toast(errorMessage(x));
      }
      invalidateScopes(['inbox', 'transactions', 'plan', 'accounts', 'activity']);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [edits, catById, toast, undo, fmt],
  );

  const approveAll = useCallback(async () => {
    const ready = items.filter(isReady);
    if (!ready.length) return;
    // Items whose category was changed locally are approved one by one so the edit is kept.
    const edited = ready.filter((it) => edits[it.id]);
    setHidden((h) => new Set([...h, ...ready.map((r) => r.id)]));
    try {
      for (const it of edited)
        await post(`/api/inbox/${it.id}/approve`, {
          categoryId: edits[it.id]?.categoryId,
          memo: edits[it.id]?.memo,
        });
      await post('/api/inbox/approve-all');
      toast(`✓ Approved ${ready.length} payment${ready.length === 1 ? '' : 's'} with suggested categories`);
    } catch (x) {
      toast(errorMessage(x));
      setHidden(new Set());
    }
    invalidateScopes(['inbox', 'transactions', 'plan', 'accounts', 'activity']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, edits, toast]);

  const linkCard = useCallback(
    async (it: InboxItemDto, accountId: string) => {
      try {
        await post(`/api/inbox/${it.id}/link-card`, { accountId });
        const a = accounts.find((x) => x.id === accountId);
        toast(`Card •• ${it.last4} now goes to ${a?.name ?? 'that account'}`);
      } catch (x) {
        toast(errorMessage(x));
      }
      invalidateScopes(['inbox', 'transactions', 'plan', 'accounts', 'settings']);
    },
    [accounts, toast],
  );

  return {
    q,
    items,
    groups,
    cats,
    catById,
    catOf,
    isReady,
    edits,
    setEdit,
    resolve,
    approveAll,
    linkCard,
    accounts,
    undo,
  };
}
type Model = ReturnType<typeof useInboxModel>;

export function InboxPage() {
  const model = useInboxModel();
  const mobile = useIsMobile();
  if (model.q.isPending) return <Spinner />;
  return mobile ? <InboxMobile m={model} /> : <InboxDesktop m={model} />;
}

function lastCaptureLine(m: Model, members: Map<string, { displayName: string }>) {
  const lc = m.q.data?.lastCapture;
  if (!lc) return null;
  return `Last capture: ${members.get(lc.memberId)?.displayName ?? 'someone'}'s phone, ${relTime(lc.at)}`;
}

function groupByDay(items: InboxItemDto[], today: string, fmt: Fmt) {
  const out: { day: string; items: InboxItemDto[] }[] = [];
  for (const it of items) {
    const d = todayIn('Europe/Belgrade', new Date(it.postedAt));
    const label = dayLabel(d, today, fmt.locale);
    const last = out[out.length - 1];
    if (last && last.day === label) last.items.push(it);
    else out.push({ day: label, items: [it] });
  }
  return out;
}

function AccountLine({ it, m }: { it: InboxItemDto; m: Model }) {
  const a = m.accounts.find((x) => x.id === it.accountId);
  if (!a)
    return (
      <span className="acct-m warn-t">
        <Icon name="card" size={14} />
        Card •• {it.last4 ?? '????'} · not linked
      </span>
    );
  const card = a.cards.find((c) => c.last4 === it.last4);
  return (
    <span className="acct-m">
      <CurrencyChip currency={a.currency} sm />
      {a.name}
      {it.last4 ? ` · ${card?.label ? `${card.label} ` : ''}•• ${it.last4}` : ''}
    </span>
  );
}

function CatChip({ it, m }: { it: InboxItemDto; m: Model }) {
  const c = m.catOf(it);
  const edited = m.edits[it.id] && 'categoryId' in m.edits[it.id]!;
  const conf = edited ? 1 : it.confidence;
  if (!c) return <span className="catchip low">Pick a category</span>;
  return (
    <span className={`catchip ${conf < 0.8 ? 'low' : ''}`}>
      {c.name}
      <Confidence value={conf} />
    </span>
  );
}

function Amount({ it, fmt }: { it: InboxItemDto; fmt: Fmt }) {
  return (
    <>
      {fmt.money(it.amount, it.currency, 2)}
      {it.currency !== fmt.base ? (
        <span className="amt2">
          ≈ {fmt.num(it.baseAmount)} {fmt.base}
        </span>
      ) : null}
    </>
  );
}

function Notice({ it, m, compact }: { it: InboxItemDto; m: Model; compact?: boolean }) {
  const { fmt } = useHousehold();
  const linkable = m.accounts
    .filter((a) => a.onBudget && !a.closed && (a.currency === it.currency || true))
    .slice(0, compact ? 3 : 3);
  if (it.needsDecision === 'duplicate' && it.duplicate) {
    return compact ? (
      <div className="note dup">
        <b>Already {it.duplicate.source.endsWith('.csv') ? 'imported from CSV' : 'recorded'}?</b> Same amount
        on {fmt.date(it.duplicate.date)} in{' '}
        {m.accounts.find((a) => a.id === it.accountId)?.name ?? 'this account'}
        <div className="acts">
          <button className="chip on" onClick={() => m.resolve(it, 'merge')}>
            Merge
          </button>
          <button className="chip" onClick={() => m.resolve(it, 'keep-both')}>
            Keep both
          </button>
        </div>
      </div>
    ) : (
      <div className="note dup">
        <Icon name="merge" size={16} />
        <span>
          <b>
            Looks like{' '}
            {it.duplicate.source.endsWith('.csv')
              ? 'the CSV import already has this'
              : 'this is already recorded'}
            .
          </b>{' '}
          {fmt.money(it.duplicate.amount, it.currency, 2)} at {it.duplicate.payeeName || it.payeeName} on{' '}
          {fmt.date(it.duplicate.date)}, from {it.duplicate.source}. Merge?
        </span>
        <span className="sp" />
        <button className="btn primary" onClick={() => m.resolve(it, 'merge')}>
          Merge
        </button>
        <button className="btn" onClick={() => m.resolve(it, 'keep-both')}>
          Keep both
        </button>
      </div>
    );
  }
  if (it.needsDecision === 'unknown_card') {
    return compact ? (
      <div className="note unk">
        <b>Card •• {it.last4} isn't linked.</b> Which account is it?
        <div className="acts">
          {linkable.map((a) => (
            <button key={a.id} className="chip" onClick={() => m.linkCard(it, a.id)}>
              {a.name}
            </button>
          ))}
        </div>
      </div>
    ) : (
      <div className="note unk">
        <Icon name="link" size={16} />
        <span>
          <b>Card •• {it.last4} isn't linked to an account.</b> Pick one, and it will be remembered.
        </span>
        <span className="sp" />
        {linkable.map((a) => (
          <button key={a.id} className="btn" onClick={() => m.linkCard(it, a.id)}>
            {a.name}
          </button>
        ))}
      </div>
    );
  }
  return null;
}

// ───────────────────────── Desktop ─────────────────────────

function InboxDesktop({ m }: { m: Model }) {
  const { fmt, today, members } = useHousehold();
  const [selIdx, setSelIdx] = useState(0);
  const [picker, setPicker] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const memoRef = useRef<HTMLInputElement>(null);
  const items = m.items;
  const sel = items[Math.min(selIdx, items.length - 1)];
  const ready = items.filter(m.isReady);
  const decisions = items.filter((i) => i.needsDecision).length;
  const lastResolved = useRef<string | null>(null);
  useEffect(() => {
    if (selIdx > items.length - 1) setSelIdx(Math.max(0, items.length - 1));
  }, [items.length, selIdx]);

  const act = (how: 'approve' | 'dismiss') => {
    if (!sel) return;
    if (how === 'approve' && (sel.needsDecision || !m.isReady(sel))) return;
    lastResolved.current = sel.id;
    void m.resolve(sel, how);
    wrapRef.current?.focus({ preventScroll: true });
  };

  const onKey = (e: React.KeyboardEvent) => {
    const t = e.target as HTMLElement;
    if (t.matches('input, textarea, select')) return;
    if (picker) return;
    const k = e.key;
    if (k === 'j' || k === 'ArrowDown') setSelIdx((i) => Math.min(items.length - 1, i + 1));
    else if (k === 'k' || k === 'ArrowUp') setSelIdx((i) => Math.max(0, i - 1));
    else if (k === 'Enter') act('approve');
    else if (k === 'x') act('dismiss');
    else if (k === 'c') setPicker(true);
    else if (k === 'e') memoRef.current?.focus();
    else if (k === 'A' && e.shiftKey) void m.approveAll();
    else if (k === 'u' && lastResolved.current) void m.undo(lastResolved.current);
    else return;
    e.preventDefault();
  };

  const groups = groupByDay(items, today, fmt);
  return (
    <div className="pg">
      <div className="pg-h">
        <div>
          <h2>Inbox</h2>
          <div className="sub">
            {items.length
              ? `${items.length} Google Pay capture${items.length === 1 ? '' : 's'} to review · ${decisions} need${decisions === 1 ? 's' : ''} a decision`
              : 'Nothing to review'}
          </div>
        </div>
        <span className="sp" />
        {ready.length ? (
          <Btn variant="primary" icon="check" onClick={() => m.approveAll()}>
            Approve all with suggested categories ({ready.length})
          </Btn>
        ) : null}
      </div>
      {items.length && sel ? (
        <div
          className="ib-wrap"
          ref={wrapRef}
          tabIndex={0}
          onKeyDown={onKey}
          aria-label="Inbox list. Use j and k to move, Enter to approve, x to dismiss, c to change category"
        >
          <div className="ib-list" role="list" aria-label="Captured payments">
            {groups.map((g) => (
              <div key={g.day} style={{ display: 'contents' }}>
                <div className="ib-day">{g.day}</div>
                {g.items.map((it) => (
                  <div
                    key={it.id}
                    id={`ib-${it.id}`}
                    role="listitem"
                    aria-current={it.id === sel.id ? 'true' : undefined}
                    className={`icard ${it.id === sel.id ? 'on' : ''}`}
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest('button')) return;
                      setSelIdx(items.indexOf(it));
                      setPicker(false);
                      wrapRef.current?.focus({ preventScroll: true });
                    }}
                  >
                    <span className="who">
                      <MerchantLogo name={it.payeeName} />
                    </span>
                    <div className="mer">
                      <b>{it.payeeName}</b>
                      <span className="raw">{it.merchantRaw}</span>
                      {it.isTest ? <span className="test-tag">Test</span> : null}
                    </div>
                    <div className="amt">
                      <Amount it={it} fmt={fmt} />
                    </div>
                    <div className="meta">
                      <AccountLine it={it} m={m} />
                      <CatChip it={it} m={m} />
                      <span className="sp" />
                      <Avatar id={it.memberId} />
                      <span>
                        {members.get(it.memberId)?.displayName ?? 'Someone'}'s{' '}
                        {it.deviceName && it.deviceName !== 'Test device' ? it.deviceName : 'phone'} ·{' '}
                        {hhmm(it.postedAt)}
                      </span>
                    </div>
                    <Notice it={it} m={m} />
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className="ib-side">
            <Detail
              it={sel}
              m={m}
              picker={picker}
              setPicker={setPicker}
              memoRef={memoRef}
              onAct={act}
              refocus={() => wrapRef.current?.focus({ preventScroll: true })}
            />
            <div className="card" style={{ marginTop: 12 }}>
              <div className="kb-legend" style={{ border: 0 }}>
                <span>
                  <Kbd>j</Kbd> <Kbd>k</Kbd> move
                </span>
                <span>
                  <Kbd>↵</Kbd> approve
                </span>
                <span>
                  <Kbd>c</Kbd> category
                </span>
                <span>
                  <Kbd>e</Kbd> memo
                </span>
                <span>
                  <Kbd>x</Kbd> dismiss
                </span>
                <span>
                  <Kbd>u</Kbd> undo
                </span>
                <span>
                  <Kbd>⇧</Kbd>
                  <Kbd>A</Kbd> approve all suggested
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <EmptyInbox desk last={lastCaptureLine(m, members)} />
      )}
    </div>
  );
}

function Detail({
  it,
  m,
  picker,
  setPicker,
  memoRef,
  onAct,
  refocus,
}: {
  it: InboxItemDto;
  m: Model;
  picker: boolean;
  setPicker: (v: boolean) => void;
  memoRef: React.RefObject<HTMLInputElement | null>;
  onAct: (how: 'approve' | 'dismiss') => void;
  refocus: () => void;
}) {
  const { fmt, members } = useHousehold();
  const c = m.catOf(it);
  const e = m.edits[it.id] ?? {};
  const changed = 'categoryId' in e && e.categoryId !== it.categoryId;
  const blocked = !!it.needsDecision || !m.isReady(it);
  return (
    <div className="card ib-det">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <MerchantLogo name={it.payeeName} size={44} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 17 }}>{it.payeeName}</div>
          <div className="raw" style={{ font: '11.5px var(--mono)', color: 'var(--ink-3)' }}>
            {it.merchantRaw}
          </div>
        </div>
        <Avatar id={it.memberId} lg />
      </div>
      <div>
        <div className="big">{fmt.money(it.amount, it.currency, 2)}</div>
        {it.currency !== fmt.base ? (
          <div className="conv">
            ≈ <b>{fmt.money(it.baseAmount, fmt.base, 2)}</b> at the NBS rate
          </div>
        ) : null}
      </div>
      <div className="fld-row">
        <span>Payee</span>
        <div className="fbox">
          {it.payeeName}
          {it.rule ? (
            <span className="muted" style={{ marginLeft: 'auto', fontSize: 12 }}>
              Rule: {it.rule.pattern} → {it.rule.payeeName}
            </span>
          ) : null}
        </div>
      </div>
      <div className="fld-row">
        <span>Category</span>
        <button
          className={`fbox ${picker ? 'focus' : ''}`}
          onClick={() => setPicker(!picker)}
          style={{ textAlign: 'left', width: '100%' }}
          aria-haspopup="listbox"
          aria-expanded={picker}
        >
          {c ? c.name : <span className="muted">Pick a category</span>}
          {c ? <Confidence value={changed ? 1 : it.confidence} /> : null}
          <span style={{ marginLeft: 'auto' }}>{c ? <CatPill c={c} /> : null}</span>
          <Kbd>c</Kbd>
        </button>
      </div>
      {picker ? (
        <CategoryPicker
          groups={m.groups}
          current={c?.id ?? null}
          onPick={(id) => {
            m.setEdit(it.id, { categoryId: id });
            setPicker(false);
            refocus();
          }}
          onClose={() => {
            setPicker(false);
            refocus();
          }}
        />
      ) : null}
      {changed && c && !it.rule ? (
        <label className="row-gap" style={{ fontSize: 12.5, color: 'var(--ink-2)', paddingLeft: 106 }}>
          <input
            type="checkbox"
            className="cb"
            checked={!!e.remember}
            onChange={(x) => m.setEdit(it.id, { remember: x.target.checked })}
          />
          Always use {c.name} for {it.payeeName}
        </label>
      ) : null}
      <div className="fld-row">
        <span>Account</span>
        <div className="fbox">
          <AccountLine it={it} m={m} />
        </div>
      </div>
      <div className="fld-row">
        <span>When</span>
        <div className="fbox">
          {fmt.date(todayIn('Europe/Belgrade', new Date(it.postedAt)))}, {hhmm(it.postedAt)} ·{' '}
          {members.get(it.memberId)?.displayName ?? 'Someone'}'s phone
        </div>
      </div>
      <div className="fld-row">
        <span>Memo</span>
        <div className="fbox">
          <input
            ref={memoRef}
            placeholder="Add memo…"
            value={e.memo ?? ''}
            onChange={(x) => m.setEdit(it.id, { memo: x.target.value })}
            onKeyDown={(x) => x.key === 'Escape' && refocus()}
            aria-label="Memo"
          />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <span style={{ flex: 1 }} />
        <button className="btn" onClick={() => onAct('dismiss')}>
          Dismiss <Kbd>x</Kbd>
        </button>
        <button className={`btn ${blocked ? '' : 'okb'}`} onClick={() => onAct('approve')} disabled={blocked}>
          <Icon name="check" size={15} stroke={2} />
          Approve <Kbd inverse>↵</Kbd>
        </button>
      </div>
    </div>
  );
}

/** Keyboard category picker: type to filter, j/k or arrows to move, ↵ picks, esc closes. */
export function CategoryPicker({
  groups,
  current,
  onPick,
  onClose,
}: {
  groups: PlanGroupDto[];
  current: string | null;
  onPick: (id: string) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const list = groups.flatMap((g) =>
    g.categories
      .filter((c) => c.name.toLowerCase().includes(q.toLowerCase()))
      .map((c) => ({ c, group: g.name })),
  );
  const [idx, setIdx] = useState(
    Math.max(
      0,
      list.findIndex((x) => x.c.id === current),
    ),
  );
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    listRef.current?.querySelector('.o.on')?.scrollIntoView({ block: 'nearest' });
  }, [idx]);
  const key = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || (e.key === 'j' && !q)) setIdx((i) => Math.min(list.length - 1, i + 1));
    else if (e.key === 'ArrowUp' || (e.key === 'k' && !q)) setIdx((i) => Math.max(0, i - 1));
    else if (e.key === 'Enter') {
      if (list[idx]) onPick(list[idx]!.c.id);
    } else if (e.key === 'Escape' || (e.key === 'c' && !q)) onClose();
    else return;
    e.preventDefault();
    e.stopPropagation();
  };
  return (
    <div className="picker" role="listbox" aria-label="Categories">
      <div className="q">
        <Icon name="search" size={15} />
        <input
          autoFocus
          placeholder="Search categories…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setIdx(0);
          }}
          onKeyDown={key}
          aria-label="Search categories"
        />
        <Kbd>esc</Kbd>
      </div>
      <div style={{ maxHeight: 300, overflow: 'auto' }} ref={listRef}>
        {list.map(({ c, group }, i) => (
          <div key={c.id}>
            {!q && (i === 0 || list[i - 1]!.group !== group) ? <div className="g">{group}</div> : null}
            <div
              className={`o ${i === idx ? 'on' : ''}`}
              role="option"
              aria-selected={i === idx}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onPick(c.id)}
            >
              <span>{c.name}</span>
              <CatPill c={c} />
            </div>
          </div>
        ))}
        {!list.length ? <div className="o muted">No match</div> : null}
      </div>
    </div>
  );
}

// ───────────────────────── Mobile ─────────────────────────

function InboxMobile({ m }: { m: Model }) {
  const { members } = useHousehold();
  const [editing, setEditing] = useState<string | null>(null);
  const [tally, setTally] = useState({ ok: 0, no: 0 });
  const items = m.items;
  const resolve = (it: InboxItemDto, how: 'approve' | 'dismiss') => {
    setTally((t) => (how === 'approve' ? { ...t, ok: t.ok + 1 } : { ...t, no: t.no + 1 }));
    void m.resolve(it, how);
  };
  const editItem = items.find((i) => i.id === editing);
  const ready = items.filter(m.isReady);
  return (
    <>
      <div className="m-h">
        <div style={{ flex: 1 }}>
          <h1>Inbox</h1>
          <div className="sub">{items.length ? `${items.length} to review` : 'Nothing to review'}</div>
        </div>
        {ready.length ? (
          <button className="btn" style={{ borderRadius: 99 }} onClick={() => m.approveAll()}>
            Approve all
          </button>
        ) : null}
      </div>
      {tally.ok || tally.no ? (
        <div className="tally">
          {tally.ok ? (
            <span className="pos-t" style={{ background: 'var(--pos-bg)' }}>
              ✓ {tally.ok} approved
            </span>
          ) : null}
          {tally.no ? <span>{tally.no} dismissed</span> : null}
        </div>
      ) : null}
      {items.length ? (
        <>
          <div className="hint">
            <Icon name="swap" size={14} />
            Swipe right to approve · left to dismiss · tap to edit
          </div>
          {items.map((it) => (
            <SwipeCard
              key={it.id}
              it={it}
              m={m}
              onResolve={(how) => resolve(it, how)}
              onTap={() => setEditing(it.id)}
            />
          ))}
        </>
      ) : (
        <div style={{ paddingTop: 60 }}>
          <EmptyInbox last={lastCaptureLine(m, members)} />
        </div>
      )}
      {editItem ? (
        <EditSheet
          it={editItem}
          m={m}
          onClose={() => setEditing(null)}
          onResolve={(how) => {
            setEditing(null);
            resolve(editItem, how);
          }}
        />
      ) : null}
    </>
  );
}

/** Swipe wrapper (design-system §6): commit past ±100px or a fling faster than 0.5px/ms. */
function SwipeCard({
  it,
  m,
  onResolve,
  onTap,
}: {
  it: InboxItemDto;
  m: Model;
  onResolve: (how: 'approve' | 'dismiss') => void;
  onTap: () => void;
}) {
  const { fmt } = useHousehold();
  const cardRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x0: number; y0: number; t0: number; dx: number; locked: boolean | null } | null>(
    null,
  );
  const [dir, setDir] = useState<'' | 'to-ok' | 'to-no'>('');
  const canApprove = m.isReady(it);
  const reduced = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  const a = m.accounts.find((x) => x.id === it.accountId);

  const onDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    drag.current = { x0: e.clientX, y0: e.clientY, t0: performance.now(), dx: 0, locked: null };
    cardRef.current!.style.transition = 'none';
  };
  const onMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x0;
    const dy = e.clientY - d.y0;
    if (d.locked === null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
      d.locked = Math.abs(dx) > Math.abs(dy);
      if (d.locked) cardRef.current!.setPointerCapture(e.pointerId);
    }
    if (!d.locked) return;
    d.dx = dx > 0 && !canApprove ? Math.min(dx, 40) : dx;
    cardRef.current!.style.transform = `translateX(${d.dx}px)${reduced ? '' : ` rotate(${d.dx / 60}deg)`}`;
    setDir(d.dx > 0 ? 'to-ok' : d.dx < 0 ? 'to-no' : '');
    wrapRef.current!.style.setProperty('--p', String(Math.min(1, Math.abs(d.dx) / 110)));
  };
  const onUp = () => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    const card = cardRef.current!;
    card.style.transition = '';
    if (d.locked === null) {
      onTap();
      return;
    }
    const v = d.dx / Math.max(1, performance.now() - d.t0);
    const commit = Math.abs(d.dx) > 100 || (Math.abs(v) > 0.5 && Math.abs(d.dx) > 30);
    const how = d.dx > 0 ? 'approve' : 'dismiss';
    if (commit && (how === 'dismiss' || canApprove)) {
      card.style.transform = `translateX(${d.dx > 0 ? 130 : -130}%)`;
      const w = wrapRef.current!;
      w.style.transition = 'height .22s .12s, margin .22s .12s, opacity .22s .12s';
      w.style.height = `${w.offsetHeight}px`;
      requestAnimationFrame(() => {
        w.style.height = '0px';
        w.style.marginBottom = '0px';
        w.style.opacity = '0';
      });
      setTimeout(() => onResolve(how), reduced ? 0 : 340);
    } else {
      card.style.transform = '';
      setDir('');
    }
  };
  return (
    <div className={`sw ${dir}`} ref={wrapRef} data-sw={it.id}>
      <div className="sw-bg" aria-hidden="true">
        <span className="ok">
          <Icon name="check" size={20} stroke={2.4} />
          Approve
        </span>
        <span className="no">
          Dismiss
          <Icon name="x" size={20} stroke={2.4} />
        </span>
      </div>
      <div
        className="sw-card"
        ref={cardRef}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        role="group"
        tabIndex={0}
        aria-label={`${it.payeeName}, ${fmt.money(it.amount, it.currency, 2)}. Tap to edit.`}
        onKeyDown={(e) => e.key === 'Enter' && onTap()}
      >
        <MerchantLogo name={it.payeeName} />
        <div className="mer">
          <b>{it.payeeName}</b> {it.isTest ? <span className="test-tag">Test</span> : null}
        </div>
        <div className="amt">{fmt.money(it.amount, it.currency, 2)}</div>
        <div className="raw">{it.merchantRaw}</div>
        <div className="amt2" style={{ textAlign: 'right' }}>
          {it.currency !== fmt.base ? `≈ ${fmt.num(it.baseAmount)} ${fmt.base}` : hhmm(it.postedAt)}
        </div>
        <div className="meta">
          <CatChip it={it} m={m} />
          <span className="sp" />
          {a ? (
            <>
              <CurrencyChip currency={a.currency} sm />
              •• {it.last4}
            </>
          ) : (
            <span className="warn-t">•• {it.last4}</span>
          )}{' '}
          <Avatar id={it.memberId} />
        </div>
        <Notice it={it} m={m} compact />
      </div>
    </div>
  );
}

function EditSheet({
  it,
  m,
  onClose,
  onResolve,
}: {
  it: InboxItemDto;
  m: Model;
  onClose: () => void;
  onResolve: (how: 'approve' | 'dismiss') => void;
}) {
  const { fmt } = useHousehold();
  const [more, setMore] = useState(false);
  const cur = m.catOf(it);
  const e = m.edits[it.id] ?? {};
  const changed = 'categoryId' in e && e.categoryId !== it.categoryId;
  const frequent = useMemo(() => {
    const ids = [
      cur?.id,
      ...m.cats
        .filter((c) => c.activity < 0)
        .sort((a, b) => a.activity - b.activity)
        .map((c) => c.id),
    ].filter((x): x is string => !!x);
    return [...new Set(ids)].slice(0, 5).map((id) => m.catById.get(id)!);
  }, [cur?.id, m.cats, m.catById]);
  const a = m.accounts.find((x) => x.id === it.accountId);
  return (
    <Sheet onClose={onClose} label={`Edit ${it.payeeName}`}>
      <div className="sh-h">
        <div>
          <small style={{ fontFamily: 'var(--mono)' }}>{it.merchantRaw}</small>
          <b>{it.payeeName}</b>
        </div>
        <b style={{ font: '600 20px var(--sans)' }}>{fmt.money(it.amount, it.currency)}</b>
      </div>
      <div className="h4" style={{ marginTop: 16 }}>
        Category
      </div>
      {more ? (
        <CategoryPicker
          groups={m.groups}
          current={cur?.id ?? null}
          onPick={(id) => {
            m.setEdit(it.id, { categoryId: id });
            setMore(false);
          }}
          onClose={() => setMore(false)}
        />
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {frequent.map((c) => (
            <button
              key={c.id}
              className={`chip ${c.id === cur?.id ? 'on' : ''}`}
              onClick={() => m.setEdit(it.id, { categoryId: c.id })}
            >
              {c.name} · {fmt.num(c.available)}
            </button>
          ))}
          <button className="chip" onClick={() => setMore(true)}>
            <Icon name="search" size={14} />
            More
          </button>
        </div>
      )}
      {changed && cur && !it.rule ? (
        <label className="row-gap" style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 10 }}>
          <input
            type="checkbox"
            className="cb"
            checked={!!e.remember}
            onChange={(x) => m.setEdit(it.id, { remember: x.target.checked })}
          />
          Always use {cur.name} for {it.payeeName}
        </label>
      ) : null}
      <div className="m-fields" style={{ margin: '16px 0 0' }}>
        <div className="m-f">
          <span>Account</span>
          <div className="v">
            {a ? (
              `${a.name} •• ${it.last4 ?? ''}`
            ) : (
              <span className="warn-t">Card •• {it.last4} not linked</span>
            )}
          </div>
        </div>
        <div className="m-f">
          <span>Memo</span>
          <div className="v">
            <input
              placeholder="Add a memo"
              value={e.memo ?? ''}
              onChange={(x) => m.setEdit(it.id, { memo: x.target.value })}
              aria-label="Memo"
            />
          </div>
        </div>
      </div>
      <div className="sh-act">
        <button className="btn big" onClick={() => onResolve('dismiss')}>
          Dismiss
        </button>
        <button className="btn big okb" onClick={() => onResolve('approve')} disabled={!m.isReady(it)}>
          <Icon name="check" size={16} stroke={2.2} />
          Approve
        </button>
      </div>
    </Sheet>
  );
}
